import { AxiosResponse } from 'axios';
import { SET_DEPARTMENTS, SET_DEPARTMENT, SET_ERRORMSG, SET_ORGANIZATIONAL_DEPARTMENT } from '@/store/mutation_types';
import {
  CLEAR_DEPARTMENT_ISLOADING,
  DELETE_DEPARTMENT,
  EDIT_DEPARTMENT,
  GET_DEPARTMENTS,
  GET_DEPARTMENT,
  GET_DEPARTMENT_IMPACT,
  REGISTER_DEPARTMENT,
  TOGGLE_DEPARTMENT_IS_ACTIVE,
  GET_ORGANIZATIONAL_DEPARTMENTS,
} from '@/store/action_types';
import Department from '@/services/department';
import { Module } from 'vuex';
import { EditDepartment, RegisterDepartment, GetDepartment } from '@/dtos/department.dto';

/**
 * The axios interceptor already pushes a human message into the error store for
 * 4xx/5xx and then resolves the promise with `undefined`. Committing
 * `result?.data?.msg` unconditionally therefore overwrote that message with
 * undefined, which is why payroll-lock (409) and validation (400) errors never
 * reached the user. Only commit when there is actually something to say.
 */
const reportError = (commit: any, result: any) => {
  const msg = result?.data?.msg;
  if (msg) commit(SET_ERRORMSG, msg);
};


/**
 * `departments` holds the FLAT list used by every filter and picker in the product;
 * `organizationalDepartments` holds the NESTED tree used by the structure page.
 *
 * These were previously the same slot: GET_ORGANIZATIONAL_DEPARTMENTS committed
 * SET_DEPARTMENTS, so opening the structure page replaced the flat list with nested
 * rows and every department filter elsewhere silently changed shape.
 */
interface Slot {
  data: any;
  isLoading: boolean;
  requestId: number;
}

const emptySlot = (): Slot => ({ data: null, isLoading: false, requestId: 0 });

const apply = (slot: Slot, payload: any) => {
  if (payload.requestId !== undefined && payload.requestId < slot.requestId) return;
  if (payload.requestId !== undefined) slot.requestId = payload.requestId;
  if ('data' in payload) slot.data = payload.data;
  slot.isLoading = Boolean(payload.isLoading);
};

const department: Module<any, {}> = {
  state: {
    department: emptySlot(),
    departments: emptySlot(),
    organizationalDepartments: emptySlot(),
  },
  getters: {
    department: (state: any) => state.department,
    departments: (state: any) => state.departments,
    organizationalDepartments: (state: any) => state.organizationalDepartments,
  },
  mutations: {
    [SET_DEPARTMENT]: (state: any, payload: any) => apply(state.department, payload),
    [SET_DEPARTMENTS]: (state: any, payload: any) => apply(state.departments, payload),
    [SET_ORGANIZATIONAL_DEPARTMENT]: (state: any, payload: any) => apply(state.organizationalDepartments, payload),
  },
  actions: {
    [GET_DEPARTMENTS]: async ({ commit, state }: any, payload: any) => {
      const requestId = state.departments.requestId + 1;
      commit(SET_DEPARTMENTS, { data: null, isLoading: true, requestId });

      try {
        const result: AxiosResponse = await Department.getDepartments(payload as GetDepartment);
        if (result?.data?.status) {
          commit(SET_DEPARTMENTS, { data: result.data.data ?? [], isLoading: false, requestId });
          return true;
        }
        commit(SET_DEPARTMENTS, { data: [], isLoading: false, requestId });
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_DEPARTMENTS, { data: [], isLoading: false, requestId });
        return false;
      }
    },

    [GET_ORGANIZATIONAL_DEPARTMENTS]: async ({ commit, state }: any, payload: any) => {
      const requestId = state.organizationalDepartments.requestId + 1;
      // The rows are deliberately left in place while refetching: nulling them blanked
      // the whole tab and unmounted the open department drawer on every save. The
      // requestId guard already discards out-of-order responses.
      commit(SET_ORGANIZATIONAL_DEPARTMENT, { isLoading: true, requestId });

      try {
        const result: AxiosResponse = await Department.getOrganizationalDepartments(payload as GetDepartment);
        if (result?.data?.status) {
          commit(SET_ORGANIZATIONAL_DEPARTMENT, { data: result.data.data ?? [], isLoading: false, requestId });
          return true;
        }
        commit(SET_ORGANIZATIONAL_DEPARTMENT, { data: [], isLoading: false, requestId });
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_ORGANIZATIONAL_DEPARTMENT, { data: [], isLoading: false, requestId });
        return false;
      }
    },

    [GET_DEPARTMENT]: async ({ commit }: any, payload: any) => {
      commit(SET_DEPARTMENT, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Department.getDepartments(payload as GetDepartment);
        if (result?.data?.status) {
          commit(SET_DEPARTMENT, { data: result.data.data?.[0] ?? null, isLoading: false });
          return true;
        }
        commit(SET_DEPARTMENT, { data: null, isLoading: false });
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_DEPARTMENT, { data: null, isLoading: false });
        return false;
      }
    },

    [REGISTER_DEPARTMENT]: async ({ commit }: any, payload: any) => {
      commit(SET_DEPARTMENT, { isLoading: true });

      try {
        const result: AxiosResponse = await Department.registerDepartment(payload as RegisterDepartment);
        commit(SET_DEPARTMENT, { data: result?.data?.data ?? null, isLoading: false });
        if (result?.data?.status) return true;
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_DEPARTMENT, { isLoading: false });
        return false;
      }
    },

    [EDIT_DEPARTMENT]: async ({ commit }: any, payload: any) => {
      commit(SET_DEPARTMENT, { isLoading: true });

      try {
        const result: AxiosResponse = await Department.editDepartment(payload as EditDepartment);
        commit(SET_DEPARTMENT, { data: result?.data?.data ?? null, isLoading: false });
        if (result?.data?.status) return true;
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_DEPARTMENT, { isLoading: false });
        return false;
      }
    },

    [TOGGLE_DEPARTMENT_IS_ACTIVE]: async ({ commit }: any, payload: any) => {
      commit(SET_DEPARTMENT, { isLoading: true });

      try {
        const result: AxiosResponse = await Department.toggleDepartmentIsActive(payload);
        commit(SET_DEPARTMENT, { isLoading: false });
        if (result?.data?.status) return true;
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_DEPARTMENT, { isLoading: false });
        return false;
      }
    },

    /** Resolves { childDepartments, positions, employees } so a delete can warn in real numbers. */
    [GET_DEPARTMENT_IMPACT]: async ({ commit }: any, payload: any) => {
      try {
        const result: AxiosResponse = await Department.getDepartmentImpact(payload);
        if (result?.data?.status) return result.data.data;
        reportError(commit, result);
        return null;
      } catch {
        return null;
      }
    },

    [DELETE_DEPARTMENT]: async ({ commit }: any, payload: string) => {
      commit(SET_DEPARTMENT, { isLoading: true });

      try {
        const result: AxiosResponse = await Department.deleteDepartment(payload);
        commit(SET_DEPARTMENT, { isLoading: false });
        if (result?.data?.status) return true;
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_DEPARTMENT, { isLoading: false });
        return false;
      }
    },

    [CLEAR_DEPARTMENT_ISLOADING]: ({ commit }: any) => {
      commit(SET_DEPARTMENT, { isLoading: false });
      commit(SET_DEPARTMENTS, { isLoading: false });
      commit(SET_ORGANIZATIONAL_DEPARTMENT, { isLoading: false });
    },
  },
};

export default department;
